import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPackageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [publications, setPublications] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    publication_id: '',
    name: '',
    price: '',
    cost_price: '',
    vendor_id: '',
    tat_days_min: '',
    tat_days_max: '',
    word_count_min: '',
    word_count_max: '',
    is_active: true
  });

  // Calculate margin preview
  const calculateMargin = () => {
    const selling = parseFloat(formData.price) || 0;
    const cost = parseFloat(formData.cost_price) || 0;
    if (selling === 0) return { margin: 0, percent: 0, partnerMargin: 0, isAtRisk: false };
    
    const margin = selling - cost;
    const percent = (margin / selling) * 100;
    const partnerMin = selling * 0.82; // 18% max discount
    const partnerMargin = partnerMin - cost;
    
    return {
      margin,
      percent: percent.toFixed(1),
      partnerMargin,
      isAtRisk: partnerMargin < 0
    };
  };

  useEffect(() => {
    fetchPublications();
    fetchVendors();
    if (isEdit) {
      fetchPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchPublications = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/publications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublications(response.data || []);
    } catch (error) {
      console.error('Error fetching publications:', error);
    }
  };

  const fetchVendors = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchPackage = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({
        ...response.data,
        cost_price: response.data.cost_price || '',
        vendor_id: response.data.vendor_id || ''
      });
    } catch (error) {
      console.error('Error fetching package:', error);
      toast.error('Failed to load package');
      navigate('/admin/packages');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.publication_id || !formData.name || !formData.price) {
      toast.error('Please fill all required fields');
      return;
    }

    const token = localStorage.getItem('token');
    const submitData = {
      ...formData,
      price: parseInt(formData.price),
      cost_price: parseFloat(formData.cost_price) || 0,
      vendor_id: formData.vendor_id || null,
      tat_days_min: parseInt(formData.tat_days_min),
      tat_days_max: parseInt(formData.tat_days_max),
      word_count_min: parseInt(formData.word_count_min),
      word_count_max: parseInt(formData.word_count_max)
    };

    try {
      setLoading(true);
      
      if (isEdit) {
        await axios.put(`${API}/admin/packages/${id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package updated successfully');
      } else {
        await axios.post(`${API}/admin/packages`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package created successfully');
      }
      
      navigate('/admin/packages');
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">
          {isEdit ? 'Edit Package' : 'Create Package'}
        </h1>
        <p className="font-manrope text-sm text-gray-600">Manage pricing package details</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Publication *</label>
              <select
                value={formData.publication_id}
                onChange={(e) => setFormData({ ...formData, publication_id: e.target.value })}
                data-testid="publication-select"
                required
                disabled={isEdit}
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none disabled:bg-gray-100"
              >
                <option value="">Select a publication</option>
                {publications.map(pub => (
                  <option key={pub.id} value={pub.id}>{pub.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Package Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Fast, Normal, Premium"
                data-testid="package-name-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Price (INR) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="25000"
                data-testid="price-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Cost & Vendor Section */}
        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Cost & Vendor</h2>
          <p className="font-manrope text-xs text-gray-500 mb-4">Track your costs to monitor margins and profitability</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Cost Price (INR)</label>
              <input
                type="number"
                value={formData.cost_price}
                onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                placeholder="15000"
                data-testid="cost-price-input"
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <p className="font-manrope text-xs text-gray-400 mt-1">What you pay to the vendor</p>
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Vendor</label>
              <select
                value={formData.vendor_id}
                onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                data-testid="vendor-select"
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              >
                <option value="">Select a vendor</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                ))}
              </select>
              <p className="font-manrope text-xs text-gray-400 mt-1">
                <a href="/admin/vendors" className="text-black underline">Manage vendors</a>
              </p>
            </div>
          </div>

          {/* Margin Preview */}
          {formData.price && formData.cost_price && (
            <div className={`mt-4 p-4 rounded ${calculateMargin().isAtRisk ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <h3 className="font-manrope text-xs uppercase tracking-widest mb-2 font-semibold">
                {calculateMargin().isAtRisk ? '⚠️ Margin Warning' : '✓ Margin Preview'}
              </h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Your Margin</p>
                  <p className={`font-bold ${calculateMargin().margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ₹{calculateMargin().margin.toLocaleString()} ({calculateMargin().percent}%)
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Partner Price (18% off)</p>
                  <p className="font-medium">₹{(parseFloat(formData.price) * 0.82).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Partner Margin</p>
                  <p className={`font-bold ${calculateMargin().partnerMargin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ₹{calculateMargin().partnerMargin.toLocaleString()}
                  </p>
                </div>
              </div>
              {calculateMargin().isAtRisk && (
                <p className="mt-2 text-xs text-red-600">
                  Warning: Partner discount would result in a loss. Consider increasing the selling price to at least ₹{Math.ceil(parseFloat(formData.cost_price) / 0.82).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Delivery & Content</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">TAT Min Days *</label>
              <input
                type="number"
                value={formData.tat_days_min}
                onChange={(e) => setFormData({ ...formData, tat_days_min: e.target.value })}
                placeholder="7"
                data-testid="tat-min-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">TAT Max Days *</label>
              <input
                type="number"
                value={formData.tat_days_max}
                onChange={(e) => setFormData({ ...formData, tat_days_max: e.target.value })}
                placeholder="10"
                data-testid="tat-max-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Word Count Min *</label>
              <input
                type="number"
                value={formData.word_count_min}
                onChange={(e) => setFormData({ ...formData, word_count_min: e.target.value })}
                placeholder="600"
                data-testid="word-min-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Word Count Max *</label>
              <input
                type="number"
                value={formData.word_count_max}
                onChange={(e) => setFormData({ ...formData, word_count_max: e.target.value })}
                placeholder="800"
                data-testid="word-max-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Settings</h2>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              data-testid="is-active-checkbox"
              className="w-4 h-4"
            />
            <div>
              <div className="font-manrope text-sm font-semibold">Active</div>
              <div className="font-manrope text-xs text-gray-600">Show this package in marketplace</div>
            </div>
          </label>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-package-button"
            className="px-8 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Package' : 'Create Package')}
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/admin/packages')}
            data-testid="cancel-button"
            className="px-8 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
          >
            Cancel
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminPackageForm;